import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { galleryApi } from '../services/gallery';
import { useAuth } from '../context/AuthContext';
import { FullscreenViewer } from '../components/common/FullscreenViewer';
import { CommentsSection } from '../components/common/CommentsSection';
import { TagsDisplay } from '../components/common/TagsDisplay';

export const GalleryPage = () => {
  const { photoId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [photos, setPhotos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Состояния для редактирования фото
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ name_photo: '', data_time: '', title: '' });
  const [isSaving, setIsSaving] = useState(false);

  const [showScrollBtn, setShowScrollBtn] = useState(true);
  const commentsRef = useRef(null);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    galleryApi.getPhotos()
      .then(allPhotos => {
        const singlePhotos = allPhotos.filter(p => !p.is_album);
        setPhotos(singlePhotos);
        const idx = singlePhotos.findIndex(p => p.pk_photo === parseInt(photoId));
        setCurrentIndex(idx >= 0 ? idx : 0);
      })
      .catch(err => {
        console.error('❌ Ошибка загрузки:', err);
        navigate('/');
      })
      .finally(() => setLoading(false));
  }, [photoId, navigate]);

  // Открытие модального окна редактирования
  const openEditModal = () => {
    const photo = photos[currentIndex];
    if (!photo) return;

    setEditForm({
      name_photo: photo.name_photo || photo.title || '',
      data_time: photo.year || '',
      title: photo.title || ''
    });
    setShowEditModal(true);
  };

  // Сохранение изменений
  const handleSavePhoto = async () => {
    if (!user?.is_admin || !photos[currentIndex]) return;

    setIsSaving(true);
    try {
      await galleryApi.updatePhoto(
        photos[currentIndex].pk_photo,
        {
          name_photo: editForm.name_photo,
          data_time: editForm.data_time,
          title: editForm.title
        },
        user.token
      );

      setShowEditModal(false);

      // Обновляем список фото
      const allPhotos = await galleryApi.getPhotos();
      const singlePhotos = allPhotos.filter(p => !p.is_album);
      setPhotos(singlePhotos);
      const idx = singlePhotos.findIndex(p => p.pk_photo === parseInt(photoId));
      setCurrentIndex(idx >= 0 ? idx : 0);
    } catch (err) {
      alert('Ошибка сохранения: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setIsDragging(false);
  }, []);

  useEffect(() => { resetZoom(); }, [currentIndex, resetZoom]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (showEditModal) setShowEditModal(false);
        else if (showDeleteConfirm) setShowDeleteConfirm(false);
        else if (isFullscreen) setIsFullscreen(false);
        else navigate('/#gallery');
      }
      if (e.key === '+' || e.key === '=') zoomIn();
      if (e.key === '-') zoomOut();
      if (e.key === '0') resetZoom();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [showEditModal, showDeleteConfirm, navigate, isFullscreen]);

  const zoomIn = () => setZoom(z => Math.min(z + 0.25, 5));
  const zoomOut = () => setZoom(z => Math.max(z - 0.25, 0.25));

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
  };

  const handleMouseDown = (e) => {
    const img = imageRef.current;
    if (!img) return;
    const imgRect = img.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    const isImageLarger = imgRect.width > (containerRect?.width || 0) || imgRect.height > (containerRect?.height || 0);

    if (zoom > 1 || isImageLarger) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    setPan({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (containerRef.current) containerRef.current.style.cursor = zoom > 1 ? 'grab' : 'zoom-in';
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.cursor = isDragging ? 'grabbing' : (zoom > 1 ? 'grab' : 'zoom-in');
    }
  }, [zoom, isDragging]);

  const handleNext = () => { if (currentIndex < photos.length - 1) setCurrentIndex(i => i + 1); };
  const handlePrev = () => { if (currentIndex > 0) setCurrentIndex(i => i - 1); };

  const handleDelete = async () => {
    if (!user?.is_admin) return;
    if (!photos[currentIndex]) return;
    setIsDeleting(true);
    try {
      await galleryApi.deletePhoto(photos[currentIndex].pk_photo, user.token);
      if (photos.length <= 1) navigate('/#gallery');
      else {
        const newPhotos = photos.filter((_, i) => i !== currentIndex);
        setPhotos(newPhotos);
        if (currentIndex >= newPhotos.length) setCurrentIndex(newPhotos.length - 1);
      }
    } catch (err) {
      alert('Ошибка: ' + err.message);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDownload = async () => {
    if (!photos[currentIndex]) return;
    const photo = photos[currentIndex];
    try {
      const res = await fetch(photo.url);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${photo.title || `photo_${currentIndex + 1}`}.jpg`;
      a.click(); URL.revokeObjectURL(url);
    } catch { window.open(photo.url, '_blank'); }
  };

  const scrollToComments = () => {
    if (commentsRef.current) {
      commentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setShowScrollBtn(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setShowScrollBtn(true), 500);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      if (scrollY < windowHeight * 0.5) {
        setShowScrollBtn(true);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handlePhotoUpdate = useCallback(() => {
    setLoading(true);
    galleryApi.getPhotos()
      .then(allPhotos => {
        const singlePhotos = allPhotos.filter(p => !p.is_album);
        setPhotos(singlePhotos);
        const idx = singlePhotos.findIndex(p => p.pk_photo === parseInt(photoId));
        setCurrentIndex(idx >= 0 ? idx : 0);
      })
      .catch(err => console.error('Ошибка обновления:', err))
      .finally(() => setLoading(false));
  }, [photoId]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-dark)', color: 'var(--text-muted)' }}>
        <span className="reveal visible">Загрузка...</span>
      </div>
    );
  }

  if (photos.length === 0) return null;

  const currentPhoto = photos[currentIndex];

  return (
    <div className="gallery-page" style={{ minHeight: '100vh', background: 'var(--bg-dark)', color: 'var(--text-light)' }}>
      {/* Верхняя панель */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        padding: '15px 25px',
        background: 'linear-gradient(to bottom, rgba(10,10,8,0.95), transparent)',
        zIndex: 100,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <button
          onClick={() => navigate('/#gallery')}
          style={{
            background: 'rgba(201, 169, 110, 0.1)',
            border: '1px solid var(--accent)',
            color: 'var(--accent)',
            padding: '10px 20px',
            cursor: 'pointer',
            fontFamily: 'Special Elite, monospace',
            fontSize: '0.85rem',
            letterSpacing: '2px',
            borderRadius: '4px',
            transition: 'all 0.3s'
          }}
        >
          ← Назад в галерею
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button onClick={zoomOut} style={{ background: 'rgba(201, 169, 110, 0.1)', border: '1px solid rgba(201, 169, 110, 0.3)', color: 'var(--text-light)', width: '38px', height: '38px', cursor: 'pointer', borderRadius: '6px', fontSize: '1rem' }}>−</button>
          <span style={{ fontFamily: 'Special Elite, monospace', fontSize: '0.85rem', color: 'var(--text-muted)', minWidth: '45px', textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
          <button onClick={zoomIn} style={{ background: 'rgba(201, 169, 110, 0.1)', border: '1px solid rgba(201, 169, 110, 0.3)', color: 'var(--text-light)', width: '38px', height: '38px', cursor: 'pointer', borderRadius: '6px', fontSize: '1rem' }}>+</button>
          <button onClick={resetZoom} style={{ background: 'rgba(201, 169, 110, 0.1)', border: '1px solid rgba(201, 169, 110, 0.3)', color: 'var(--text-light)', width: '38px', height: '38px', cursor: 'pointer', borderRadius: '6px', fontSize: '1rem' }}>⟲</button>
          <div style={{ width: '1px', height: '24px', background: 'rgba(201, 169, 110, 0.3)', margin: '0 4px' }} />

          {/* 🔥 Кнопка редактирования (только для админов) */}
          {user?.is_admin && (
            <button
              onClick={openEditModal}
              style={{ background: 'rgba(201, 169, 110, 0.15)', border: '1px solid rgba(201, 169, 110, 0.4)', color: 'var(--accent)', width: '38px', height: '38px', cursor: 'pointer', borderRadius: '6px', fontSize: '1rem' }}
              title="Редактировать фото"
            >
              ✏️
            </button>
          )}

          {user?.is_admin && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{ background: 'rgba(231, 76, 60, 0.15)', border: '1px solid rgba(231, 76, 60, 0.4)', color: '#e74c3c', width: '38px', height: '38px', cursor: 'pointer', borderRadius: '6px', fontSize: '1rem' }}
              title="Удалить фото"
            >
              🗑
            </button>
          )}
          <button onClick={handleDownload} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg-dark)', padding: '0 16px', cursor: 'pointer', borderRadius: '6px', fontFamily: 'Special Elite, monospace', fontSize: '0.85rem', letterSpacing: '1px' }}>⬇ Скачать</button>
          <button
            onClick={() => setIsFullscreen(true)}
            style={{ background: 'rgba(201, 169, 110, 0.15)', border: '1px solid rgba(201, 169, 110, 0.3)', color: 'var(--text-light)', width: '38px', height: '38px', cursor: 'pointer', borderRadius: '6px', fontSize: '1rem' }}
            title="Полный экран (F11)"
          >
            ⛶
          </button>
        </div>
      </div>

      {/* Область просмотра */}
      <div
        ref={containerRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          position: 'fixed', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
          userSelect: 'none',
          paddingTop: '80px', paddingBottom: '80px'
        }}
      >
        <img
          ref={imageRef}
          src={currentPhoto.url}
          alt={currentPhoto.title || 'Фотография'}
          style={{
            maxWidth: '95vw',
            maxHeight: '85vh',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            filter: 'contrast(1.05) sepia(0.1)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            borderRadius: '4px'
          }}
        />
      </div>

      {/* Навигация */}
      <button
        onClick={handlePrev}
        disabled={currentIndex === 0}
        style={{
          position: 'fixed', left: '25px', top: '50%', transform: 'translateY(-50%)',
          background: 'rgba(10, 10, 8, 0.7)',
          border: '1px solid rgba(201, 169, 110, 0.3)',
          width: '55px', height: '55px',
          cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
          opacity: currentIndex === 0 ? '0.3' : '1',
          borderRadius: '50%',
          zIndex: 50,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
      >
        <div style={{ width: '12px', height: '12px', borderTop: '2px solid var(--text-light)', borderRight: '2px solid var(--text-light)', transform: 'rotate(-135deg)', marginLeft: '3px' }} />
      </button>

      <button
        onClick={handleNext}
        disabled={currentIndex === photos.length - 1}
        style={{
          position: 'fixed', right: '25px', top: '50%', transform: 'translateY(-50%)',
          background: 'rgba(10, 10, 8, 0.7)',
          border: '1px solid rgba(201, 169, 110, 0.3)',
          width: '55px', height: '55px',
          cursor: currentIndex === photos.length - 1 ? 'not-allowed' : 'pointer',
          opacity: currentIndex === photos.length - 1 ? '0.3' : '1',
          borderRadius: '50%',
          zIndex: 50,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
      >
        <div style={{ width: '12px', height: '12px', borderTop: '2px solid var(--text-light)', borderRight: '2px solid var(--text-light)', transform: 'rotate(45deg)', marginRight: '3px' }} />
      </button>

      {/* Информация о фото */}
      <div style={{
        position: 'fixed', bottom: '25px', left: '50%', transform: 'translateX(-50%)',
        fontFamily: 'Special Elite, monospace',
        fontSize: '0.85rem',
        color: 'var(--text-muted)',
        letterSpacing: '3px',
        background: 'rgba(10, 10, 8, 0.85)',
        padding: '8px 20px',
        borderRadius: '20px',
        border: '1px solid rgba(201, 169, 110, 0.2)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
      }}>
        <span>
          {String(currentIndex + 1).padStart(2, '0')} / {String(photos.length).padStart(2, '0')}
          {currentPhoto.title && <span style={{ marginLeft: '10px', color: 'var(--text-light)' }}>• {currentPhoto.title}</span>}
        </span>

        {/* 🔥 Отображение тегов */}
        {currentPhoto.tags && currentPhoto.tags.length > 0 && (
          <div style={{ display: 'flex', gap: '6px' }}>
            {currentPhoto.tags.map(tag => (
              <span key={tag.pk_tag} style={{
                background: 'rgba(155, 89, 182, 0.2)',
                border: '1px solid rgba(155, 89, 182, 0.4)',
                color: '#9b59b6',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '0.75rem'
              }}>
                {tag.title}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Плавающая кнопка "К комментариям" */}
      {showScrollBtn && (
        <button
          onClick={scrollToComments}
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.95) 0%, rgba(180, 150, 90, 0.95) 100%)',
            border: '1px solid rgba(201, 169, 110, 0.6)',
            color: 'var(--bg-dark)',
            padding: '14px 22px',
            cursor: 'pointer',
            borderRadius: '30px',
            fontFamily: 'Special Elite, monospace',
            fontSize: '0.9rem',
            fontWeight: 600,
            letterSpacing: '1px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(201, 169, 110, 0.3)',
            zIndex: 90,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
          }}
          title="Перейти к комментариям"
        >
          💬 Комментарии
          <span style={{ fontSize: '1.1rem', transform: 'translateY(2px)' }}>↓</span>
        </button>
      )}

      {/*  МОДАЛЬНОЕ ОКНО РЕДАКТИРОВАНИЯ ФОТО */}
      {showEditModal && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(5, 5, 3, 0.9)',
          zIndex: 100000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(5px)'
        }} onClick={() => setShowEditModal(false)}>
          <div style={{
            background: 'linear-gradient(160deg, #1c1c16 0%, #12120e 100%)',
            border: '1.5px solid rgba(201, 169, 110, 0.6)',
            borderRadius: '16px',
            padding: '35px 40px',
            maxWidth: '500px',
            width: '90vw',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.7)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: '1.6rem',
              color: 'var(--accent)',
              marginBottom: '25px',
              margin: '0 0 25px 0'
            }}>
              ✏️ Редактировать фото
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '8px', fontFamily: 'Special Elite, monospace' }}>
                Название фото
              </label>
              <input
                type="text"
                value={editForm.name_photo}
                onChange={e => setEditForm({ ...editForm, name_photo: e.target.value })}
                placeholder="Введите название"
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(201, 169, 110, 0.3)',
                  borderRadius: '8px',
                  color: 'var(--text-light)',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '8px', fontFamily: 'Special Elite, monospace' }}>
                Дата (ГГГГ-ММ-ДД)
              </label>
              <input
                type="date"
                value={editForm.data_time}
                onChange={e => setEditForm({ ...editForm, data_time: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(201, 169, 110, 0.3)',
                  borderRadius: '8px',
                  color: 'var(--text-light)',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '8px', fontFamily: 'Special Elite, monospace' }}>
                Название альбома/группы
              </label>
              <input
                type="text"
                value={editForm.title}
                onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                placeholder="Название группы фото"
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(201, 169, 110, 0.3)',
                  borderRadius: '8px',
                  color: 'var(--text-light)',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'var(--text-muted)',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Special Elite, monospace',
                  fontSize: '0.9rem'
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleSavePhoto}
                disabled={isSaving}
                style={{
                  background: 'var(--accent)',
                  border: 'none',
                  color: 'var(--bg-dark)',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontFamily: 'Special Elite, monospace',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  opacity: isSaving ? 0.6 : 1
                }}
              >
                {isSaving ? 'Сохранение...' : '💾 Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Подтверждение удаления */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(5, 5, 3, 0.9)',
          zIndex: 100000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: 'var(--bg-medium)',
            border: '2px solid rgba(231, 76, 60, 0.5)',
            padding: '35px',
            textAlign: 'center',
            maxWidth: '420px',
            borderRadius: '10px',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.8)'
          }}>
            <h3 style={{ color: '#e74c3c', marginBottom: '10px', fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem' }}>Удалить фото?</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: '1.6', marginBottom: '30px' }}>
              "{currentPhoto.title || `Фото #${currentIndex + 1}`}" будет удалено безвозвратно.
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                style={{
                  background: '#e74c3c', color: 'white', border: '1px solid #e74c3c',
                  padding: '12px 28px', cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontFamily: 'Special Elite, monospace', fontSize: '0.85rem', letterSpacing: '1.5px',
                  textTransform: 'uppercase', borderRadius: '6px',
                  opacity: isDeleting ? '0.5' : '1'
                }}
              >
                {isDeleting ? 'Удаление...' : 'Да, удалить'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                style={{
                  background: 'transparent', border: '1px solid var(--text-muted)',
                  color: 'var(--text-muted)', padding: '12px 28px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontFamily: 'Special Elite, monospace', fontSize: '0.85rem', letterSpacing: '1.5px',
                  textTransform: 'uppercase', borderRadius: '6px'
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Полноэкранный просмотрщик */}
      {isFullscreen && currentPhoto && (
        <FullscreenViewer
          imageUrl={currentPhoto.url}
          title={currentPhoto.title}
          onClose={() => setIsFullscreen(false)}
        />
      )}

      {/* Секция комментариев */}
      <div
        ref={commentsRef}
        style={{
          position: 'relative',
          marginTop: '100vh',
          padding: '40px 20px 80px',
          background: 'var(--bg-dark)',
          zIndex: 10,
          minHeight: '60vh'
        }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <CommentsSection
            photoId={currentPhoto.pk_photo}
            photoData={currentPhoto}
            onPhotoUpdate={handlePhotoUpdate}
          />
        </div>

        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            background: 'rgba(20, 20, 15, 0.95)',
            border: '1px solid rgba(201, 169, 110, 0.5)',
            color: 'var(--accent)',
            padding: '14px 22px',
            cursor: 'pointer',
            borderRadius: '30px',
            fontFamily: 'Special Elite, monospace',
            fontSize: '0.9rem',
            letterSpacing: '1px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            zIndex: 90,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.15)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.background = 'rgba(20, 20, 15, 0.95)';
          }}
          title="Вернуться к просмотру фото"
        >
          <span style={{ fontSize: '1.1rem', transform: 'translateY(-2px)' }}>↑</span>
          К фотографии
        </button>
      </div>
    </div>
  );
};